/**
 * ProgressBar.tsx
 *
 * A configurable step-progress indicator for the no-code builder.
 *
 * Builder mode  → shows controls for label, color, style
 * Journey mode  → renders an animated progress bar
 *
 * When used inside JourneyPage, the `currentStep` and `totalSteps` props
 * are injected automatically from the journey's checkpoint_template.
 * The builder can also set a static label override.
 *
 * Value shape:
 *   {
 *     label?: string;          // e.g. "Personal Information" — overrides auto label
 *     color?: string;          // hex color for the filled bar
 *     showStepCount?: boolean; // show "Step 2 of 8" text
 *     style?: 'bar' | 'dots' | 'steps';
 *   }
 *
 * Runtime props injected by JourneyPage (not stored in value):
 *   currentStep?: number;
 *   totalSteps?: number;
 */

import React from 'react';

// ─── Types ────────────────────────────────────────────────────────────────────

interface ProgressBarValue {
  label?: string;
  color?: string;
  showStepCount?: boolean;
  style?: 'bar' | 'dots' | 'steps';
}

interface ProgressBarProps {
  value?: ProgressBarValue;
  onChange?: (val: ProgressBarValue) => void;
  disabled?: boolean;
  isBuilderMode?: boolean;
  /** Injected by JourneyPage from checkpoint_template */
  currentStep?: number;
  totalSteps?: number;
}

// ─── Color presets ────────────────────────────────────────────────────────────

const COLOR_PRESETS = [
  { label: 'Green', value: '#16a34a' },
  { label: 'Blue', value: '#2563eb' },
  { label: 'Purple', value: '#7c3aed' },
  { label: 'Orange', value: '#ea580c' },
  { label: 'Pink', value: '#db2777' },
  { label: 'Teal', value: '#0d9488' },
];

// ─── Sub-renderers ────────────────────────────────────────────────────────────

const BarProgress: React.FC<{ pct: number; color: string }> = ({ pct, color }) => (
  <div className="w-full h-2 bg-gray-200 rounded-full overflow-hidden">
    <div
      className="h-full rounded-full transition-all duration-700 ease-out"
      style={{ width: `${pct}%`, backgroundColor: color }}
    />
  </div>
);

const DotsProgress: React.FC<{ current: number; total: number; color: string }> = ({ current, total, color }) => (
  <div className="flex items-center justify-center gap-2">
    {Array.from({ length: total }).map((_, i) => (
      <div
        key={i}
        className="rounded-full transition-all duration-300"
        style={{
          width: i === current - 1 ? 20 : 8,
          height: 8,
          backgroundColor: i < current ? color : '#e5e7eb',
        }}
      />
    ))}
  </div>
);

const StepsProgress: React.FC<{ current: number; total: number; color: string }> = ({ current, total, color }) => (
  <div className="flex items-center w-full">
    {Array.from({ length: total }).map((_, i) => {
      const done = i < current;
      const active = i === current - 1;
      return (
        <React.Fragment key={i}>
          <div
            className="flex-shrink-0 w-6 h-6 rounded-full flex items-center justify-center text-xs font-bold transition-all duration-300"
            style={{
              backgroundColor: done || active ? color : '#e5e7eb',
              color: done || active ? '#fff' : '#9ca3af',
              transform: active ? 'scale(1.2)' : 'scale(1)',
            }}
          >
            {done && !active ? (
              <svg width="10" height="10" viewBox="0 0 10 10" fill="none">
                <path d="M2 5l2.5 2.5L8 3" stroke="white" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
            ) : (
              i + 1
            )}
          </div>
          {i < total - 1 && (
            <div
              className="flex-1 h-0.5 transition-all duration-500"
              style={{ backgroundColor: done ? color : '#e5e7eb' }}
            />
          )}
        </React.Fragment>
      );
    })}
  </div>
);

// ─── Main Component ───────────────────────────────────────────────────────────

const ProgressBar: React.FC<ProgressBarProps> = ({
  value = {},
  onChange,
  disabled = false,
  isBuilderMode = false,
  currentStep,
  totalSteps,
}) => {
  const label = value.label ?? '';
  const color = value.color ?? '#16a34a';
  const showStepCount = value.showStepCount ?? true;
  const style = value.style ?? 'bar';

  // Use injected runtime values, fall back to builder preview values
  const current = currentStep ?? 3;
  const total = totalSteps ?? 8;
  const pct = total > 0 ? Math.round((current / total) * 100) : 0;

  const update = (patch: Partial<ProgressBarValue>) => onChange?.({ ...value, ...patch });

  // ── Journey mode ────────────────────────────────────────────────────────────
  if (!isBuilderMode) {
    return (
      <div className="w-full space-y-1.5 py-1">
        {(label || showStepCount) && (
          <div className="flex items-center justify-between">
            {label ? (
              <span className="text-xs font-medium text-gray-600">{label}</span>
            ) : <span />}
            {showStepCount && (
              <span className="text-xs text-gray-400 font-medium">
                Step {current} of {total}
              </span>
            )}
          </div>
        )}
        {style === 'bar' && <BarProgress pct={pct} color={color} />}
        {style === 'dots' && <DotsProgress current={current} total={total} color={color} />}
        {style === 'steps' && <StepsProgress current={current} total={total} color={color} />}
      </div>
    );
  }

  // ── Builder mode ────────────────────────────────────────────────────────────
  return (
    <div className="space-y-3 p-1">
      {/* Live preview */}
      <div className="space-y-1">
        <label className="text-xs font-semibold text-gray-400 uppercase tracking-wide select-none">
          Preview (Step {current} of {total})
        </label>
        <div className="border border-gray-200 rounded-lg p-3 bg-gray-50 space-y-1.5">
          {(label || showStepCount) && (
            <div className="flex items-center justify-between">
              {label ? <span className="text-xs font-medium text-gray-600">{label}</span> : <span />}
              {showStepCount && (
                <span className="text-xs text-gray-400 font-medium">Step {current} of {total}</span>
              )}
            </div>
          )}
          {style === 'bar' && <BarProgress pct={pct} color={color} />}
          {style === 'dots' && <DotsProgress current={current} total={total} color={color} />}
          {style === 'steps' && <StepsProgress current={current} total={total} color={color} />}
        </div>
      </div>

      {/* Style selector */}
      <div className="space-y-1">
        <label className="text-xs font-semibold text-gray-400 uppercase tracking-wide select-none">
          Style
        </label>
        <div className="flex gap-2">
          {(['bar', 'dots', 'steps'] as const).map(s => (
            <button
              key={s}
              type="button"
              onClick={() => update({ style: s })}
              disabled={disabled}
              className={[
                'flex-1 py-1.5 text-xs rounded border capitalize transition-colors',
                style === s
                  ? 'bg-gray-800 text-white border-gray-800'
                  : 'bg-white text-gray-600 border-gray-200 hover:border-gray-400',
              ].join(' ')}
            >
              {s}
            </button>
          ))}
        </div>
      </div>

      {/* Color */}
      <div className="space-y-1">
        <label className="text-xs font-semibold text-gray-400 uppercase tracking-wide select-none">
          Color
        </label>
        <div className="flex gap-1.5 flex-wrap">
          {COLOR_PRESETS.map(c => (
            <button
              key={c.value}
              type="button"
              title={c.label}
              onClick={() => update({ color: c.value })}
              disabled={disabled}
              className={[
                'w-6 h-6 rounded-full border-2 transition-transform hover:scale-110',
                color === c.value ? 'border-gray-800 scale-110' : 'border-transparent',
              ].join(' ')}
              style={{ backgroundColor: c.value }}
            />
          ))}
          <input
            type="color"
            value={color}
            onChange={e => update({ color: e.target.value })}
            disabled={disabled}
            title="Custom color"
            className="w-6 h-6 rounded-full cursor-pointer border border-gray-200 p-0"
          />
        </div>
      </div>

      {/* Label override */}
      <div className="space-y-1">
        <label className="text-xs font-semibold text-gray-400 uppercase tracking-wide select-none">
          Label (optional)
        </label>
        <input
          type="text"
          value={label}
          onChange={e => update({ label: e.target.value })}
          disabled={disabled}
          placeholder="e.g. Personal Information"
          className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm text-gray-800
                     focus:outline-none focus:ring-2 focus:ring-intellend-green
                     disabled:bg-gray-50 disabled:text-gray-500 disabled:cursor-not-allowed"
        />
      </div>

      {/* Show step count toggle */}
      <div className="flex items-center gap-2">
        <button
          type="button"
          role="switch"
          aria-checked={showStepCount}
          onClick={() => update({ showStepCount: !showStepCount })}
          disabled={disabled}
          className={[
            'relative inline-flex h-5 w-9 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200',
            showStepCount ? 'bg-gray-800' : 'bg-gray-200',
            disabled ? 'opacity-50 cursor-not-allowed' : '',
          ].join(' ')}
        >
          <span
            className={[
              'pointer-events-none inline-block h-4 w-4 transform rounded-full bg-white shadow ring-0 transition duration-200',
              showStepCount ? 'translate-x-4' : 'translate-x-0',
            ].join(' ')}
          />
        </button>
        <span className="text-xs text-gray-600 select-none">Show "Step X of Y" counter</span>
      </div>
    </div>
  );
};

export default ProgressBar;
